import { AxiosError } from 'axios';
import { useCallback, useEffect } from 'react';

import api, { restApi } from '../services/api';
import { isSelfHandledForbidden } from '../services/selfHandledForbidden.utils';
import { BaseErrorResponseData } from '../types/request.types';

const TOKEN_VALIDATION_PERIOD = 50000;

type AuthenticationInterceptorProps = {
  onLogout?: () => void;
  onForbiddenError?: (error?: BaseErrorResponseData) => void;
};

const useAuthenticationInterceptor = ({
  onLogout,
  onForbiddenError,
}: AuthenticationInterceptorProps) => {
  const onErrorInterceptor = useCallback(
    (error: AxiosError<BaseErrorResponseData>) => {
      const status = error.response ? error.response.status : null;
      if (status === 401 && onLogout) {
        onLogout();
      }

      if (status === 403 && onForbiddenError && !isSelfHandledForbidden(error.config?.url)) {
        onForbiddenError(error.response?.data);
      }

      return Promise.reject(error);
    },
    [onForbiddenError, onLogout],
  );

  useEffect(() => {
    const interceptorId = restApi.axiosInstance.interceptors.response.use(
      (response) => response,
      onErrorInterceptor,
    );
    return () => restApi.axiosInstance.interceptors.response.eject(interceptorId);
  }, [onErrorInterceptor]);

  useEffect(() => {
    const timeout = setTimeout(api.ping, TOKEN_VALIDATION_PERIOD);
    return () => clearTimeout(timeout);
  }, []);

  return null;
};

export default useAuthenticationInterceptor;
